/**
 * ECharts time formatters pinned to Europe/Ljubljana (Nova Gorica MONG pilot).
 *
 * ECharts `type:'time'` axes label ticks + tooltips in the browser's local
 * timezone by default. These helpers force Europe/Ljubljana so the chart agrees
 * with the panel table and the CSV/XLSX exports, regardless of the viewer's
 * machine timezone. Shared by NgArsoPanel + NgPrometPanel.
 */

import { formatLjubljanaTs } from '../../../../../shared/utils/ljubljanaTime.js';

export type ChartGranularity = 'date' | 'datetime';

const HTML_ESCAPE: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/** Escape text for the HTML tooltip. Series names come from DB/external metadata. */
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => HTML_ESCAPE[c] ?? c);
}

/** ECharts time-axis tick-label formatter pinned to Europe/Ljubljana. */
export function ljubljanaAxisLabel(granularity: ChartGranularity): (value: number) => string {
  return (value: number) => formatLjubljanaTs(new Date(value).toISOString(), granularity);
}

/**
 * Crosshair axisPointer label formatter. A `cross` pointer labels BOTH axes
 * with the same formatter: only the time axis (x) holds an instant — a value
 * axis shows its number. (Formatting a y value as a time printed
 * »1970-01-01 01:00« beside the value axis, #720.)
 */
export function ljubljanaAxisPointerLabel(): (p: { value: number | string | Date; axisDimension?: string }) => string {
  return (p) => {
    if (p.axisDimension !== undefined && p.axisDimension !== 'x') {
      const n = Number(p.value);
      return Number.isFinite(n) ? n.toLocaleString('sl-SI', { maximumFractionDigits: 1 }) : String(p.value);
    }
    // ECharts' axisPointer label value can be number | string | Date; the Date
    // constructor accepts all three.
    return formatLjubljanaTs(new Date(p.value).toISOString(), 'datetime');
  };
}

interface TooltipItem {
  axisValue?: number | string;
  value?: unknown;
  seriesName?: string;
  marker?: string;
}

/**
 * Tooltip formatter for a `trigger:'axis'` time chart that pins the header time
 * to Europe/Ljubljana. `valueFmt` renders each series' y-value (panels differ:
 * pollutant decimals vs traffic counts). Param typed `unknown` so it stays
 * assignable to ECharts' formatter type without leaking `any`.
 */
export function ljubljanaTooltipFormatter(
  granularity: ChartGranularity,
  valueFmt: (v: number | null) => string,
): (params: unknown) => string {
  return (params: unknown) => {
    const list = (Array.isArray(params) ? params : [params]) as TooltipItem[];
    const first = list[0];
    const tsMs = Number(
      first?.axisValue ?? (Array.isArray(first?.value) ? (first?.value as unknown[])[0] : NaN),
    );
    const header = Number.isFinite(tsMs)
      ? formatLjubljanaTs(new Date(tsMs).toISOString(), granularity)
      : '';
    const rows = list.map((it) => {
      const raw = Array.isArray(it.value) ? (it.value as unknown[])[1] : it.value;
      const num = typeof raw === 'number' ? raw : raw == null ? null : Number(raw);
      const value = num !== null && Number.isFinite(num) ? num : null;
      // marker is ECharts' own HTML (colored dot); seriesName is external → escape it.
      return `${it.marker ?? ''}${escapeHtml(it.seriesName ?? '')}: ${valueFmt(value)}`;
    });
    return [header, ...rows].join('<br/>');
  };
}
